import { RefreshCw } from '@hotel-manager/ui/icons';
/**
 * F2-a: dashboard owner — vista "oggi" dei 3 hotel reali.
 *
 * useQuery(['owner','dashboard'], fetchOwnerDashboard, refetchInterval 30s):
 * read-only + auto-refresh, sempre live (nessuna cache dati, vedi PWA D2).
 * Header "I tuoi hotel" + griglia di HotelTodayCard. Mobile-first: 1 colonna
 * su mobile, 2-3 su desktop. Chrome owner dedicato (NON il layout operatore).
 */
import { useQuery } from '@tanstack/react-query';
import { fetchOwnerDashboard } from '../../api/owner.js';
import { useOwnerSession } from '../../stores/owner.js';
import { HotelTodayCard } from './HotelTodayCard.js';
import { OwnerLogoutButton } from './OwnerLogoutButton.js';

export function OwnerDashboardPage() {
  const owner = useOwnerSession((s) => s.owner);
  const query = useQuery({
    queryKey: ['owner', 'dashboard'],
    queryFn: fetchOwnerDashboard,
    refetchInterval: 30_000,
  });

  const hotels = query.data?.hotels ?? [];

  return (
    <main className="min-h-screen bg-bg-base text-text-primary font-sans antialiased">
      {/* Topbar owner */}
      <header className="sticky top-0 z-10 bg-bg-topbar/95 backdrop-blur border-b border-border-base">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <span className="w-9 h-9 rounded-lg bg-accent-primary text-text-on-accent flex items-center justify-center text-sm font-bold shrink-0">
              HM
            </span>
            <div className="min-w-0">
              <div className="text-sm font-bold leading-tight tracking-hm-tight">
                Area Proprietario
              </div>
              {owner && <div className="text-[11px] text-text-muted truncate">{owner.email}</div>}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => query.refetch()}
              disabled={query.isFetching}
              aria-label="Aggiorna"
              className="inline-flex items-center gap-1.5 rounded-lg border border-border-base bg-bg-card px-3 h-9 text-[13px] font-medium text-text-secondary hover:bg-bg-muted transition-colors disabled:opacity-50"
            >
              <RefreshCw
                size={14}
                strokeWidth={1.75}
                className={query.isFetching ? 'animate-spin' : ''}
              />
              <span className="hidden sm:inline">Aggiorna</span>
            </button>
            <OwnerLogoutButton />
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="flex items-end justify-between gap-3 mb-5">
          <div>
            <h1 className="text-2xl font-bold tracking-hm-tight">I tuoi hotel</h1>
            <p className="text-sm text-text-secondary mt-0.5">
              Stato operativo di oggi · aggiornamento automatico ogni 30s
            </p>
          </div>
        </div>

        {query.isPending && <DashboardSkeleton />}

        {query.isError && !query.data && (
          <div
            role="alert"
            className="rounded-2xl border border-border-base bg-bg-card p-8 text-center shadow-hm-sm"
          >
            <p className="text-text-primary font-semibold">Impossibile caricare la dashboard.</p>
            <p className="text-sm text-text-secondary mt-1">Controlla la connessione e riprova.</p>
            <button
              type="button"
              onClick={() => query.refetch()}
              className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-accent-primary text-text-on-accent px-4 h-10 text-sm font-semibold hover:bg-accent-primary-hover transition-colors"
            >
              <RefreshCw size={14} strokeWidth={1.75} />
              Riprova
            </button>
          </div>
        )}

        {query.data && hotels.length === 0 && (
          <div className="rounded-2xl border border-border-base bg-bg-card p-8 text-center shadow-hm-sm">
            <p className="text-text-secondary">Nessun hotel disponibile.</p>
          </div>
        )}

        {hotels.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {hotels.map((h) => (
              <HotelTodayCard key={h.tenantId} hotel={h} />
            ))}
          </div>
        )}
      </div>
    </main>
  );
}

function DashboardSkeleton() {
  return (
    <div
      data-testid="dashboard-skeleton"
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
    >
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="rounded-2xl border border-border-base bg-bg-card shadow-hm-sm p-5 animate-pulse"
        >
          <div className="h-5 w-2/3 rounded bg-bg-muted" />
          <div className="h-16 w-full rounded-xl bg-bg-muted mt-4" />
          <div className="h-3 w-full rounded bg-bg-muted mt-4" />
          <div className="h-3 w-1/2 rounded bg-bg-muted mt-3" />
        </div>
      ))}
    </div>
  );
}
